/**
 * Firebase Storage Attachment Adapter — uploads files to the backend,
 * which stores them in Firebase Storage and returns URLs.
 *
 * Implements the assistant-ui AttachmentAdapter interface.
 * Sends auth tokens, returns structured metadata for rich previews.
 *
 * Uploads start eagerly in add() so the file is already on the server
 * by the time the user presses send — eliminating the upload delay.
 */
import type { AttachmentAdapter } from '@assistant-ui/react';
import { toast } from '@/components/ui/Toast';

type UploadResult = {
  url: string;
  name: string;
  contentType: string;
  size: number;
  storagePath: string;
};

type PendingAttachment = {
  id: string;
  type: 'image' | 'document' | 'file';
  name: string;
  contentType: string;
  file: File;
  status: { type: 'requires-action'; reason: 'composer-send' };
};

type CompleteAttachment = {
  id: string;
  type: 'image' | 'document' | 'file';
  name: string;
  contentType: string;
  content: Array<{ type: 'file'; data: string; mimeType: string } | { type: 'text'; text: string }>;
  status: { type: 'complete' };
};

/** What the upload route takes (agent-core `ai-chat.upload.ts`, `ALLOWED_MIMES`): keep the two in step. */
const UPLOADABLE_TYPES = new Set([
  'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
  'text/plain', 'text/csv', 'text/html', 'text/markdown',
  'application/json', 'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
]);

/**
 * The file as it will be uploaded, or null when the server won't take it.
 * Other text (code, `text/x-python`, ...) goes up as plain text under its own name.
 */
function uploadable(file: File): File | null {
  if (UPLOADABLE_TYPES.has(file.type)) return file;
  if (file.type.startsWith('text/')) return new globalThis.File([file], file.name, { type: 'text/plain' });
  return null;
}

function getAttachmentType(contentType: string): 'image' | 'document' | 'file' {
  if (contentType.startsWith('image/')) return 'image';
  if (
    contentType === 'application/pdf' ||
    contentType === 'text/plain' ||
    contentType === 'text/csv' ||
    contentType === 'text/markdown' ||
    contentType === 'application/json'
  ) return 'document';
  return 'file';
}

type TokenProvider = () => Promise<string | null>;

export class StorageAttachmentAdapter implements AttachmentAdapter {
  accept = 'image/*,text/*,.csv,.json,.pdf,.xlsx';

  private _getToken: TokenProvider;
  private _pending = new Map<string, Promise<UploadResult>>();

  constructor(getToken?: TokenProvider) {
    this._getToken = getToken ?? (async () => null);
  }

  async add({ file: original }: { file: File }): Promise<PendingAttachment> {
    const file = uploadable(original);
    if (!file) {
      toast.error(`${original.name} can't be attached: ${original.type || 'this file type'} is not supported.`);
      throw new Error(`Unsupported attachment type: ${original.type || original.name}`);
    }
    const id = `attachment-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const uploadPromise = this._upload(file);
    uploadPromise.catch(() => {}); // reported by send()
    this._pending.set(id, uploadPromise);

    return {
      id,
      type: getAttachmentType(file.type),
      name: file.name,
      contentType: file.type,
      file,
      status: { type: 'requires-action', reason: 'composer-send' },
    };
  }

  /**
   * Never throws: the composer is already cleared when this runs, and one
   * failed upload would drop the whole message. A failed file goes as a note.
   */
  async send(attachment: PendingAttachment): Promise<CompleteAttachment> {
    const uploadPromise = this._pending.get(attachment.id);
    this._pending.delete(attachment.id);

    let data: UploadResult;
    try {
      data = uploadPromise
        ? await uploadPromise
        : await this._upload(attachment.file);
    } catch (err) {
      toast.error(`${attachment.name} could not be uploaded: ${(err as Error).message}`);
      return {
        id: attachment.id,
        type: attachment.type,
        name: attachment.name,
        contentType: attachment.contentType,
        content: [{ type: 'text', text: `[${attachment.name} was attached but could not be uploaded]` }],
        status: { type: 'complete' },
      };
    }

    return {
      id: attachment.id,
      type: attachment.type,
      name: data.name,
      contentType: data.contentType,
      content: [
        { type: 'file', data: data.url, mimeType: data.contentType },
      ],
      status: { type: 'complete' },
    };
  }

  async remove(attachment: PendingAttachment): Promise<void> {
    this._pending.delete(attachment.id);
  }

  private async _upload(file: File): Promise<UploadResult> {
    const formData = new FormData();
    formData.append('file', file);

    const headers: Record<string, string> = {};
    const token = await this._getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch('/api/chat/upload', {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.error?.message ?? (response.statusText || `HTTP ${response.status}`));
    }

    const json = await response.json();
    return json.data as UploadResult;
  }
}
